import React, { useEffect, useState } from "react";
import "./WFHRequestsPage.css";
import { getRole } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";

/* ================= HELPERS ================= */
const normalizeStatus = (status) =>
  (status || "").toString().trim().toUpperCase();

const calculateDays = (start, end) =>
  Math.ceil(
    (new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)
  ) + 1;

/* ================= COMPONENT ================= */
const WFHRequestsPage = () => {
  const userRole = getRole();

  /* ================= STATE ================= */
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Confirm popup
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [actionData, setActionData] = useState(null);

  // Result popup (success / error)
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  /* ================= FETCH REQUESTS ================= */
  const fetchRequests = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const data = await apiClient("/wfh/requests/");
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      setRequests(list);
      setFilteredRequests(list);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to fetch WFH requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  /* ================= FILTER ================= */
  useEffect(() => {
    const priority = { PENDING: 1, APPROVED: 2, REJECTED: 3 };
    let filtered = [...requests];

    if (statusFilter) {
      filtered = filtered.filter(
        (r) => normalizeStatus(r.status) === statusFilter
      );
    }

    if (nameFilter) {
      filtered = filtered.filter((r) =>
        r.employee_name?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter((r) =>
        r.employee_department
          ?.toLowerCase()
          .includes(departmentFilter.toLowerCase())
      );
    }

    filtered.sort(
      (a, b) =>
        (priority[normalizeStatus(a.status)] || 99) -
        (priority[normalizeStatus(b.status)] || 99)
    );

    setFilteredRequests(filtered);
  }, [statusFilter, nameFilter, departmentFilter, requests]);

  /* ================= ACTION HANDLING ================= */
  const confirmAction = (id, newStatus) => {
    setActionData({ id, newStatus });
    setShowConfirmPopup(true);
  };

  const handleAction = async () => {
    if (!actionData) return;

    const { id, newStatus } = actionData;

    setShowConfirmPopup(false);
    setActionLoading(true);

    try {
      await apiClient(`/wfh/requests/${id}/action/`, {
        method: "PATCH",
        body: JSON.stringify({ action: newStatus }),
      });

      // Refresh list (page refresh feel)
      await fetchRequests();

      setResultMessage(
        `WFH request ${newStatus.toLowerCase()} successfully.`
      );
      setShowResultPopup(true);
    } catch (err) {
      setResultMessage(
        err?.error || err?.detail || "Failed to update WFH request."
      );
      setShowResultPopup(true);
    } finally {
      setActionLoading(false);
      setActionData(null);
    }
  };

  /* ================= COUNTS ================= */
  const totalCount = requests.length;
  const pendingCount = requests.filter(
    (r) => normalizeStatus(r.status) === "PENDING"
  ).length;
  const approvedCount = requests.filter(
    (r) => normalizeStatus(r.status) === "APPROVED"
  ).length;
  const rejectedCount = requests.filter(
    (r) => normalizeStatus(r.status) === "REJECTED"
  ).length;

  /* ================= UI ================= */
  return (
    <div className="wfh-dashboard">
      <h2>WFH Requests</h2>

      {/* COUNTERS */}
      <div className="request-tiles12">
        <div className="tile12 total">Total: {totalCount}</div>
        <div className="tile12 pending">Pending: {pendingCount}</div>
        <div className="tile12 approved">Approved: {approvedCount}</div>
        <div className="tile12 rejected">Rejected: {rejectedCount}</div>
      </div>

      {/* FILTERS */}
      <div className="filters-row1">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>

        <input
          placeholder="Search by Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />

        <input
          placeholder="Search by Department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        />
      </div>

      {loading && <p>Loading WFH requests...</p>}
      {errorMsg && <p className="error-message">{errorMsg}</p>}

      {/* TABLE */}
      <div className="table-container">
        <table className="leave-table4">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Start</th>
              <th>End</th>
              <th>Days</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredRequests.length ? (
              filteredRequests.map((req) => {
                const status = normalizeStatus(req.status);

                return (
                  <tr key={req.id}>
                    <td>{req.employee_name}</td>
                    <td>{req.employee_department}</td>
                    <td>{req.start_date}</td>
                    <td>{req.end_date}</td>
                    <td>{calculateDays(req.start_date, req.end_date)}</td>
                    <td>{req.reason}</td>
                    <td className={`status-${status.toLowerCase()}`}>
                      {status}
                    </td>
                    <td>
                      {status === "PENDING" && userRole !== "employee" && (
                        <>
                          <button
                            className="btn-approve"
                            onClick={() =>
                              confirmAction(req.id, "APPROVED")
                            }
                            disabled={actionLoading}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() =>
                              confirmAction(req.id, "REJECTED")
                            }
                            disabled={actionLoading}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No WFH requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* CONFIRM POPUP */}
      {showConfirmPopup && (
        <div className="modal15-overlay">
          <div className="modal15-content">
            <p>
              Are you sure you want to{" "}
              <strong>
                {actionData?.newStatus === "APPROVED"
                  ? "approve"
                  : "reject"}
              </strong>{" "}
              this request?
            </p>

            <div className="modal15-actions">
              <button
                className="btn15-confirm"
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? "Processing..." : "Yes"}
              </button>

              <button
                className="btn15-cancel"
                onClick={() => setShowConfirmPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESULT POPUP */}
      {showResultPopup && (
        <div className="modal15-overlay">
          <div className="modal15-content">
            <p>{resultMessage}</p>

            <div className="modal15-actions">
              <button
                className="btn15-confirm"
                onClick={() => setShowResultPopup(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WFHRequestsPage;
